import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode;
}

/**
 * 制限付きログイン中でも到達を許すパス（課金管理・データ取り出し・パスワード変更）。
 *
 * `/attendance-export` を含めること。サーバー側は `/api/attendance/export/jobs` を
 * `requireExportAccess`（削除予定日を過ぎたかだけを見る）に置いて解約後も通しており、
 * `AttendanceExportPage` にも `/api/users` が 403 のときの簡易モードが実装済み。
 * ここに入れ忘れると、サブスクリプション画面が案内している「出勤簿の出力」に
 * 到達できず（押しても同じ画面へ差し戻される）、
 * 「解約日から30日間ダウンロードできる」という規約が履行不能になる。
 */
const RESTRICTED_ALLOWED_PATHS = ['/subscription', '/change-password', '/attendance-export'];

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>読み込み中...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // ログイン後に元のページに戻れるよう、現在のパスを保存
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // パスワードリセットが必要な場合、パスワード変更画面へリダイレクト
  if (user?.passwordResetRequired && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // 制限付きログイン（#11）: 解約済み・トライアル切れ・決済猶予切れ。
  // サーバー側は商品 API を 403 で塞いでいるので、そのまま商品画面へ行かせると
  // エラーだらけの画面を見せることになる。行き先を課金画面に寄せる。
  // ここは「データを取り出す」「支払って再開する」という2つの正当な操作への導線。
  if (user?.accessRestricted && !RESTRICTED_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/subscription" replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;